import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveOrg } from "@/hooks/useActiveOrg";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Plus, BookOpen, Wand2, Save } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface Subject {
  id: string;
  name: string;
  grade: string;
}

interface Category {
  id: string;
  name: string;
  description?: string;
}

export default function InstructorTestCreate() {
  const { user } = useAuth();
  const { activeOrgId } = useActiveOrg();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    subject: "",
    grade: "",
    topic: "",
    timeLimit: "60",
  });

  useEffect(() => {
    loadSubjects();
  }, []);

  useEffect(() => {
    if (formData.subject && formData.grade) {
      loadCategories();
    }
  }, [formData.subject, formData.grade]);

  const loadSubjects = async () => {
    try {
      const { data, error } = await supabase
        .from('subjects')
        .select('*')
        .order('grade', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setSubjects(data || []);
    } catch (error) {
      console.error('Error loading subjects:', error);
      toast.error('Failed to load subjects');
    }
  };

  const loadCategories = async () => {
    if (!formData.subject) return;
    
    try {
      // First find the subject ID by name if formData.subject is a name
      let subjectId = formData.subject;
      const matchingSubject = subjects.find(s => s.name === formData.subject);
      if (matchingSubject) {
        subjectId = matchingSubject.id;
      }

      const { data, error } = await supabase
        .from('subjects_categories')
        .select('*')
        .eq('subject_id', subjectId)
        .order('name', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateTest = async () => {
    if (!user) return;

    if (!formData.title.trim() || !formData.subject || !formData.grade || !formData.topic.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('instructor_tests')
        .insert({
          instructor_id: user.id,
          org_id: activeOrgId ?? null,
          title: formData.title.trim(),
          description: formData.description.trim(),
          subject: formData.subject,
          grade: formData.grade,
          topic: formData.topic.trim(),
          time_limit: parseInt(formData.timeLimit) || null,
          total_questions: 0,
          is_published: false
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Test created successfully!');
      navigate(`/instructor/tests/${data.id}/edit`);
    } catch (error) {
      console.error('Error creating test:', error);
      toast.error('Failed to create test');
    } finally {
      setLoading(false);
    }
  };

  const grades = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  const subjectsForGrade = subjects.filter(s => s.grade === formData.grade);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Create New Test
          </h1>
          <p className="text-muted-foreground">
            Set up a new test with custom questions and invite students to participate.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Test Details
                </CardTitle>
                <CardDescription>
                  Provide basic information about your test
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Test Title *</Label>
                  <Input
                    id="title"
                    placeholder="Enter test title (e.g., 'Grade 6 Math - Fractions Quiz')"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe what this test covers..."
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="grade">Grade Level *</Label>
                    <Select onValueChange={(value) => handleInputChange('grade', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {grades.map((grade) => (
                          <SelectItem key={grade} value={grade}>
                            Grade {grade}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Select 
                      onValueChange={(value) => handleInputChange('subject', value)}
                      disabled={!formData.grade}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjectsForGrade.map((subject) => (
                          <SelectItem key={subject.id} value={subject.name}>
                            {subject.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="topic">Topic *</Label>
                    <Input
                      id="topic"
                      placeholder="Enter topic (e.g., 'Fractions and Decimals')"
                      value={formData.topic}
                      onChange={(e) => handleInputChange('topic', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeLimit">Time Limit (minutes)</Label>
                    <Select onValueChange={(value) => handleInputChange('timeLimit', value)} defaultValue="60">
                      <SelectTrigger>
                        <SelectValue placeholder="Select time limit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="45">45 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                        <SelectItem value="90">90 minutes</SelectItem>
                        <SelectItem value="120">2 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Next Steps</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  After creating your test, you'll be able to:
                </div>
                <ul className="text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <Plus className="h-4 w-4 mt-0.5 text-primary" />
                    Add questions manually or use AI generation
                  </li>
                  <li className="flex items-start gap-2">
                    <Wand2 className="h-4 w-4 mt-0.5 text-primary" />
                    Mix different question types (MCQ, matching, puzzles)
                  </li>
                  <li className="flex items-start gap-2">
                    <BookOpen className="h-4 w-4 mt-0.5 text-primary" />
                    Preview and organize questions
                  </li>
                  <li className="flex items-start gap-2">
                    <Save className="h-4 w-4 mt-0.5 text-primary" />
                    Publish and invite students
                  </li>
                </ul>
                
                <Separator />
                
                <Button 
                  onClick={handleCreateTest}
                  disabled={loading || !formData.title.trim() || !formData.subject || !formData.grade || !formData.topic.trim()}
                  className="w-full"
                >
                  {loading ? 'Creating...' : 'Create Test & Continue'}
                </Button>
              </CardContent>
            </Card>

            {categories.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Available Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {categories.slice(0, 10).map((category) => (
                      <Badge key={category.id} variant="outline" className="text-xs">
                        {category.name}
                      </Badge>
                    ))}
                  </div>
                  {categories.length > 10 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      +{categories.length - 10} more categories available
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}